import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { TranslocoService } from '@jsverse/transloco';
import { cloneDeep } from 'lodash-es';
import { SsPermissionsClaims } from '../../../core/auth/auth.types';
import { UserService } from '../../../core/user/user.service';
import { LicenseService } from '../../../modules/backoffice/license/license.service';
import { SS_SUPPORTED_MODULES } from '../../../modules/backoffice/license/modules/modules.constants';
import { ExternalModulesService } from '../../../modules/external/external-modules.service';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    userService = inject(UserService);
    licenseService = inject(LicenseService);
    externalModulesService = inject(ExternalModulesService);
    private readonly _compactNavigation: FuseNavigationItem[] = [
        {
            id: 'home',
            title: 'home',
            type: 'basic',
            icon: 'heroicons_outline:chart-pie',
            link: '/home',
        },
        {
            id: 'configuration',
            title: 'configuration',
            type: 'basic',
            icon: 'heroicons_outline:cog-6-tooth',
            link: '/configuration',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Configuration),
        },
        {
            id: 'basic-master-data',
            title: 'basic-master-data',
            type: 'collapsable',
            icon: 'heroicons_outline:inbox',
            hidden: (masterDataItem) => this.allChildrenHidden(masterDataItem),
            children: [
                {
                    id: 'countries',
                    title: 'countries',
                    type: 'basic',
                    icon: 'heroicons_outline:globe-alt',
                    link: '/countries',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Countries),
                },
                {
                    id: 'currencies',
                    title: 'currencies',
                    type: 'basic',
                    icon: 'heroicons_outline:currency-euro',
                    link: '/currencies',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Currencies),
                },
                {
                    id: 'payment-types',
                    title: 'payment-types',
                    type: 'basic',
                    icon: 'heroicons_outline:credit-card',
                    link: '/payment-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.PaymentTypes),
                },
                {
                    id: 'vats',
                    title: 'vats',
                    type: 'basic',
                    icon: 'heroicons_outline:chart-bar-square',
                    link: '/vats',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Vats),
                },
                {
                    id: 'meas-units',
                    title: 'meas-units',
                    type: 'basic',
                    icon: 'heroicons_outline:queue-list',
                    link: '/meas-units',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.MeasUnits),
                },
                {
                    id: 'printer-types',
                    title: 'printer-types',
                    type: 'basic',
                    icon: 'heroicons_outline:printer',
                    link: '/printer-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
                {
                    id: 'print-formats',
                    title: 'print-formats',
                    type: 'basic',
                    icon: 'heroicons_outline:document-text',
                    link: '/print-formats',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
            ],
        },
        {
            id: 'accounts',
            title: 'accounts-section',
            type: 'collapsable',
            icon: 'heroicons_outline:user-circle',
            hidden: (accountItem) => this.allChildrenHidden(accountItem),
            children: [
                {
                    id: 'roles',
                    title: 'roles',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-group',
                    link: '/roles',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Roles),
                },
                {
                    id: 'users',
                    title: 'users',
                    type: 'basic',
                    icon: 'heroicons_outline:users',
                    link: '/accounts',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Users),
                },
                {
                    id: 'api-keys',
                    title: 'api-keys',
                    type: 'basic',
                    icon: 'heroicons_outline:key',
                    link: '/api-keys',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.ApiKeys),
                },
            ],
        },
        {
            id: 'business-entities-section',
            title: 'business-entities-section',
            type: 'collapsable',
            icon: 'heroicons_outline:building-office',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'brands',
                    title: 'brands',
                    type: 'basic',
                    icon: 'heroicons_outline:briefcase',
                    link: '/brands',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Brands),
                },
                {
                    id: 'companies',
                    title: 'companies',
                    type: 'basic',
                    icon: 'heroicons_outline:building-office-2',
                    link: '/companies',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Companies),
                },
                {
                    id: 'shops',
                    title: 'shops',
                    type: 'basic',
                    icon: 'heroicons_outline:building-storefront',
                    link: '/shops',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Shops),
                },
                {
                    id: 'pos',
                    title: 'pos',
                    type: 'basic',
                    icon: 'heroicons_outline:banknotes',
                    link: '/pos',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Pos),
                },
                {
                    id: 'rooms',
                    title: 'rooms',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/rooms',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Rooms),
                },
                {
                    id: 'production-stations',
                    title: 'production-stations',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-2x2',
                    link: '/production-stations',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
            ],
        },
        {
            id: 'article-categories',
            title: 'article-categories',
            type: 'basic',
            icon: 'mat_outline:account_tree',
            link: '/article-categories',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.ArticleCategories),
        },
        {
            id: 'articles',
            title: 'articles',
            type: 'basic',
            icon: 'heroicons_outline:square-3-stack-3d',
            link: '/articles',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Articles),
        },
        {
            id: 'recipes',
            title: 'recipes',
            type: 'basic',
            icon: 'heroicons_outline:queue-list',
            link: '/recipes',
            hidden: () =>
                !this.userService.hasPermission(SsPermissionsClaims.Recipes) ||
                !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.RESTAURANT),
        },
        {
            id: 'purchases-section',
            title: 'purchases-section.purchases',
            type: 'collapsable',
            icon: 'heroicons_outline:inbox-arrow-down',
            hidden: (item) =>
                this.allChildrenHidden(item) || !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
            children: [
                {
                    id: 'suppliers',
                    title: 'purchases-section.suppliers',
                    type: 'basic',
                    icon: 'heroicons_outline:truck',
                    link: '/suppliers',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Suppliers),
                },
                {
                    id: 'purchase-price-lists',
                    title: 'purchases-section.purchase-price-lists',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/purchase-price-lists',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.PurchasePriceLists),
                },
            ],
        },
        {
            id: 'sell-section',
            title: 'sell-section',
            type: 'collapsable',
            icon: 'heroicons_outline:calculator',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'sale-price-lists',
                    title: 'sale-price-lists',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/sale-price-lists',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SalePriceLists),
                },
                {
                    id: 'sale-promotions',
                    title: 'sale-promotions',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/sale-promotions',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.SalePromotions) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.PROMOTIONS),
                },
                {
                    id: 'coupon-templates',
                    title: 'coupon-templates',
                    type: 'basic',
                    icon: 'heroicons_outline:calculator',
                    link: '/coupon-templates',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Coupons) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.COUPONS),
                },
                {
                    id: 'upsells',
                    title: 'upsells',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/upsells',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Upsells),
                },
                {
                    id: 'out-of-stock',
                    title: 'out-of-stock',
                    type: 'basic',
                    icon: 'heroicons_outline:archive-box-x-mark',
                    link: '/out-of-stock',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.OutOfStock),
                },
                // Moved out of the Kiosk group by docs/pos/18: these three configure what every
                // selling device offers, totem and cash register alike, so they belong to Vendite
                // and are gated on the permission alone (no Kiosk module check).
                {
                    id: 'sale-groups',
                    title: 'sales.groups',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-plus',
                    link: '/sales/groups',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
                {
                    id: 'sale-layouts',
                    title: 'sales.layouts',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-stack',
                    link: '/sales/layouts',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
                {
                    id: 'sale-layout-schedules',
                    title: 'sales.layout-schedules',
                    type: 'basic',
                    icon: 'heroicons_outline:calendar-days',
                    link: '/sales/layout-schedules',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
            ],
        },
        {
            id: 'customer-section',
            title: 'customers.customer-section',
            type: 'collapsable',
            icon: 'heroicons_outline:user-group',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'customers',
                    title: 'customers.customers',
                    type: 'basic',
                    icon: 'heroicons_outline:user-group',
                    link: '/customers',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Customers),
                },
                {
                    id: 'customer-card-types',
                    title: 'customers.card-types',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-stack',
                    link: '/customer-card-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SalePriceLists),
                },
                {
                    id: 'customer-points-collections',
                    title: 'customers.points-collections',
                    type: 'basic',
                    icon: 'heroicons_outline:calendar-days',
                    link: '/customer-points-collections',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.CustomerPointsCollections),
                },
                {
                    id: 'legal-documents',
                    title: 'customers.legal-documents',
                    type: 'basic',
                    icon: 'heroicons_outline:document',
                    link: '/legal-documents',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.LegalDocuments),
                },
            ],
        },
        {
            id: 'documents',
            title: 'documents.documents',
            type: 'collapsable',
            icon: 'heroicons_outline:document-duplicate',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'documents',
                    title: 'documents.list',
                    type: 'basic',
                    icon: 'heroicons_outline:clipboard-document-list',
                    link: '/documents/manage',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Documents) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
                },
                {
                    id: 'documents-causals',
                    title: 'documents.documents-causals',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-2x2',
                    link: '/documents/causals',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.DocumentCausals),
                },
                {
                    id: 'document-history',
                    title: 'documents.history',
                    type: 'basic',
                    icon: 'heroicons_outline:clock',
                    link: '/document-history',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Documents) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
                },
                {
                    id: 'bills',
                    title: 'documents.bills',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/bills',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Bills),
                },
            ],
        },
        {
            id: 'prints-section',
            title: 'prints-section',
            type: 'collapsable',
            icon: 'heroicons_outline:printer',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'reports',
                    title: 'reports',
                    type: 'basic',
                    icon: 'heroicons_outline:document-chart-bar',
                    link: '/reports',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Reports),
                },
                {
                    id: 'label-print-spool',
                    title: 'label-print-spool',
                    type: 'basic',
                    icon: 'heroicons_outline:tag',
                    link: '/label-print-spool',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Articles),
                },
            ],
        },
        {
            id: 'kiosk',
            title: 'kiosk.kiosk',
            type: 'collapsable',
            icon: 'heroicons_outline:device-tablet',
            hidden: (item) =>
                this.allChildrenHidden(item) || !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.KIOSK),
            children: [
                {
                    id: 'kiosk-tile-templates',
                    title: 'kiosk.tile-templates',
                    type: 'basic',
                    icon: 'heroicons_outline:paint-brush',
                    link: '/kiosk/tile-templates',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.KioskApplicationData),
                },
                {
                    id: 'kiosk-media-schedule',
                    title: 'kiosk.media-schedule',
                    type: 'basic',
                    icon: 'heroicons_outline:photo',
                    link: '/kiosk/media-schedule',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.KioskApplicationData),
                },
            ],
        },
        {
            id: 'fidelity',
            title: 'fidelity.fidelity',
            type: 'collapsable',
            icon: 'heroicons_outline:credit-card',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'fidelity-marketing-contents',
                    title: 'fidelity.marketing-contents',
                    type: 'basic',
                    icon: 'heroicons_outline:rocket-launch',
                    link: '/fidelity/marketing-contents',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.FidelityApplicationData) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.FIDELITY),
                },
                {
                    id: 'fidelity-surveys',
                    title: 'fidelity.surveys',
                    type: 'basic',
                    icon: 'heroicons_outline:document-text',
                    link: '/fidelity/surveys',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.FidelityApplicationData) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.SURVEYS),
                },
            ],
        },
        {
            id: 'tags',
            title: 'tags',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: '/tags',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Tags),
        },
    ];
    private readonly _defaultNavigation: FuseNavigationItem[] = [
        {
            id: 'home',
            title: 'home',
            type: 'basic',
            icon: 'heroicons_outline:chart-pie',
            link: '/home',
        },
        {
            id: 'configuration',
            title: 'configuration',
            type: 'basic',
            icon: 'heroicons_outline:cog-6-tooth',
            link: '/configuration',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Configuration),
        },
        {
            id: 'basic-master-data',
            title: 'basic-master-data',
            type: 'collapsable',
            icon: 'heroicons_outline:inbox',
            hidden: (masterDataItem) => this.allChildrenHidden(masterDataItem),
            children: [
                {
                    id: 'countries',
                    title: 'countries',
                    type: 'basic',
                    icon: 'heroicons_outline:globe-alt',
                    link: '/countries',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Countries),
                },
                {
                    id: 'currencies',
                    title: 'currencies',
                    type: 'basic',
                    icon: 'heroicons_outline:currency-euro',
                    link: '/currencies',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Currencies),
                },
                {
                    id: 'payment-types',
                    title: 'payment-types',
                    type: 'basic',
                    icon: 'heroicons_outline:credit-card',
                    link: '/payment-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.PaymentTypes),
                },
                {
                    id: 'vats',
                    title: 'vats',
                    type: 'basic',
                    icon: 'heroicons_outline:chart-bar-square',
                    link: '/vats',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Vats),
                },
                {
                    id: 'meas-units',
                    title: 'meas-units',
                    type: 'basic',
                    icon: 'heroicons_outline:queue-list',
                    link: '/meas-units',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.MeasUnits),
                },
                {
                    id: 'printer-types',
                    title: 'printer-types',
                    type: 'basic',
                    icon: 'heroicons_outline:printer',
                    link: '/printer-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
                {
                    id: 'print-formats',
                    title: 'print-formats',
                    type: 'basic',
                    icon: 'heroicons_outline:document-text',
                    link: '/print-formats',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
            ],
        },
        {
            id: 'accounts',
            title: 'accounts-section',
            type: 'collapsable',
            icon: 'heroicons_outline:user-circle',
            hidden: (accountItem) => this.allChildrenHidden(accountItem),
            children: [
                {
                    id: 'roles',
                    title: 'roles',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-group',
                    link: '/roles',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Roles),
                },
                {
                    id: 'users',
                    title: 'users',
                    type: 'basic',
                    icon: 'heroicons_outline:users',
                    link: '/accounts',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Users),
                },
                {
                    id: 'api-keys',
                    title: 'api-keys',
                    type: 'basic',
                    icon: 'heroicons_outline:key',
                    link: '/api-keys',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.ApiKeys),
                },
            ],
        },
        {
            id: 'business-entities-section',
            title: 'business-entities-section',
            type: 'collapsable',
            icon: 'heroicons_outline:building-office',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'brands',
                    title: 'brands',
                    type: 'basic',
                    icon: 'heroicons_outline:briefcase',
                    link: '/brands',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Brands),
                },
                {
                    id: 'companies',
                    title: 'companies',
                    type: 'basic',
                    icon: 'heroicons_outline:building-office-2',
                    link: '/companies',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Companies),
                },
                {
                    id: 'shops',
                    title: 'shops',
                    type: 'basic',
                    icon: 'heroicons_outline:building-storefront',
                    link: '/shops',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Shops),
                },
                {
                    id: 'pos',
                    title: 'pos',
                    type: 'basic',
                    icon: 'heroicons_outline:banknotes',
                    link: '/pos',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Pos),
                },
                {
                    id: 'rooms',
                    title: 'rooms',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/rooms',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Rooms),
                },
                {
                    id: 'production-stations',
                    title: 'production-stations',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-2x2',
                    link: '/production-stations',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Printers),
                },
            ],
        },
        {
            id: 'article-categories',
            title: 'article-categories',
            type: 'basic',
            icon: 'mat_outline:account_tree',
            link: '/article-categories',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.ArticleCategories),
        },
        {
            id: 'articles',
            title: 'articles',
            type: 'basic',
            icon: 'heroicons_outline:square-3-stack-3d',
            link: '/articles',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Articles),
        },
        {
            id: 'recipes',
            title: 'recipes',
            type: 'basic',
            icon: 'heroicons_outline:queue-list',
            link: '/recipes',
            hidden: () =>
                !this.userService.hasPermission(SsPermissionsClaims.Recipes) ||
                !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.RESTAURANT),
        },
        {
            id: 'purchases-section',
            title: 'purchases-section.purchases',
            type: 'collapsable',
            icon: 'heroicons_outline:inbox-arrow-down',
            hidden: (item) =>
                this.allChildrenHidden(item) || !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
            children: [
                {
                    id: 'suppliers',
                    title: 'purchases-section.suppliers',
                    type: 'basic',
                    icon: 'heroicons_outline:truck',
                    link: '/suppliers',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Suppliers),
                },
                {
                    id: 'purchase-price-lists',
                    title: 'purchases-section.purchase-price-lists',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/purchase-price-lists',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.PurchasePriceLists),
                },
            ],
        },
        {
            id: 'sell-section',
            title: 'sell-section',
            type: 'collapsable',
            icon: 'heroicons_outline:calculator',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'sale-price-lists',
                    title: 'sale-price-lists',
                    type: 'basic',
                    icon: 'heroicons_outline:table-cells',
                    link: '/sale-price-lists',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SalePriceLists),
                },
                {
                    id: 'sale-promotions',
                    title: 'sale-promotions',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/sale-promotions',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.SalePromotions) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.PROMOTIONS),
                },
                {
                    id: 'coupon-templates',
                    title: 'coupon-templates',
                    type: 'basic',
                    icon: 'heroicons_outline:calculator',
                    link: '/coupon-templates',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Coupons) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.COUPONS),
                },
                {
                    id: 'upsells',
                    title: 'upsells',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/upsells',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Upsells),
                },
                {
                    id: 'out-of-stock',
                    title: 'out-of-stock',
                    type: 'basic',
                    icon: 'heroicons_outline:archive-box-x-mark',
                    link: '/out-of-stock',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.OutOfStock),
                },
                // Moved out of the Kiosk group by docs/pos/18: these three configure what every
                // selling device offers, totem and cash register alike, so they belong to Vendite
                // and are gated on the permission alone (no Kiosk module check).
                {
                    id: 'sale-groups',
                    title: 'sales.groups',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-plus',
                    link: '/sales/groups',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
                {
                    id: 'sale-layouts',
                    title: 'sales.layouts',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-stack',
                    link: '/sales/layouts',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
                {
                    id: 'sale-layout-schedules',
                    title: 'sales.layout-schedules',
                    type: 'basic',
                    icon: 'heroicons_outline:calendar-days',
                    link: '/sales/layout-schedules',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.SaleApplicationData),
                },
            ],
        },
        {
            id: 'customer-section',
            title: 'customers.customer-section',
            type: 'collapsable',
            icon: 'heroicons_outline:user-group',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'customers',
                    title: 'customers.customers',
                    type: 'basic',
                    icon: 'heroicons_outline:user-group',
                    link: '/customers',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Customers),
                },
                {
                    id: 'customer-card-types',
                    title: 'customers.card-types',
                    type: 'basic',
                    icon: 'heroicons_outline:rectangle-stack',
                    link: '/customer-card-types',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Customers),
                },
                {
                    id: 'customer-points-collections',
                    title: 'customers.points-collections',
                    type: 'basic',
                    icon: 'heroicons_outline:calendar-days',
                    link: '/customer-points-collections',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.CustomerPointsCollections),
                },
                {
                    id: 'legal-documents',
                    title: 'customers.legal-documents',
                    type: 'basic',
                    icon: 'heroicons_outline:document',
                    link: '/legal-documents',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.LegalDocuments),
                },
            ],
        },
        {
            id: 'documents',
            title: 'documents.documents',
            type: 'collapsable',
            icon: 'heroicons_outline:document-duplicate',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'documents',
                    title: 'documents.list',
                    type: 'basic',
                    icon: 'heroicons_outline:clipboard-document-list',
                    link: '/documents/manage',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Documents) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
                },
                {
                    id: 'documents-causals',
                    title: 'documents.documents-causals',
                    type: 'basic',
                    icon: 'heroicons_outline:squares-2x2',
                    link: '/documents/causals',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.DocumentCausals),
                },
                {
                    id: 'document-history',
                    title: 'documents.history',
                    type: 'basic',
                    icon: 'heroicons_outline:clock',
                    link: '/document-history',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.Documents) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.WAREHOUSE),
                },
                {
                    id: 'bills',
                    title: 'documents.bills',
                    type: 'basic',
                    icon: 'heroicons_outline:receipt-percent',
                    link: '/bills',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Bills),
                },
            ],
        },
        {
            id: 'prints-section',
            title: 'prints-section',
            type: 'collapsable',
            icon: 'heroicons_outline:printer',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'reports',
                    title: 'reports',
                    type: 'basic',
                    icon: 'heroicons_outline:document-chart-bar',
                    link: '/reports',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Reports),
                },
                {
                    id: 'label-print-spool',
                    title: 'label-print-spool',
                    type: 'basic',
                    icon: 'heroicons_outline:tag',
                    link: '/label-print-spool',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Articles),
                },
            ],
        },
        {
            id: 'kiosk',
            title: 'kiosk.kiosk',
            type: 'collapsable',
            icon: 'heroicons_outline:device-tablet',
            hidden: (item) =>
                this.allChildrenHidden(item) || !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.KIOSK),
            children: [
                {
                    id: 'kiosk-tile-templates',
                    title: 'kiosk.tile-templates',
                    type: 'basic',
                    icon: 'heroicons_outline:paint-brush',
                    link: '/kiosk/tile-templates',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.KioskApplicationData),
                },
                {
                    id: 'kiosk-media-schedule',
                    title: 'kiosk.media-schedule',
                    type: 'basic',
                    icon: 'heroicons_outline:photo',
                    link: '/kiosk/media-schedule',
                    hidden: () => !this.userService.hasPermission(SsPermissionsClaims.KioskApplicationData),
                },
            ],
        },
        {
            id: 'fidelity',
            title: 'fidelity.fidelity',
            type: 'collapsable',
            icon: 'heroicons_outline:credit-card',
            hidden: (item) => this.allChildrenHidden(item),
            children: [
                {
                    id: 'fidelity-marketing-contents',
                    title: 'fidelity.marketing-contents',
                    type: 'basic',
                    icon: 'heroicons_outline:rocket-launch',
                    link: '/fidelity/marketing-contents',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.FidelityApplicationData) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.FIDELITY),
                },
                {
                    id: 'fidelity-surveys',
                    title: 'fidelity.surveys',
                    type: 'basic',
                    icon: 'heroicons_outline:document-text',
                    link: '/fidelity/surveys',
                    hidden: () =>
                        !this.userService.hasPermission(SsPermissionsClaims.FidelityApplicationData) ||
                        !this.licenseService.isModuleActive(SS_SUPPORTED_MODULES.SURVEYS),
                },
            ],
        },
        {
            id: 'tags',
            title: 'tags',
            type: 'basic',
            icon: 'heroicons_outline:tag',
            link: '/tags',
            hidden: () => !this.userService.hasPermission(SsPermissionsClaims.Tags),
        },
    ];

    /**
     * List of external modules to associate a specific icon for.
     * @private
     */
    private static readonly EXTERNAL_MODULE_ICONS: Record<string, string> = {
        booking: 'heroicons_outline:calendar-days',
    };

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _translocoService: TranslocoService
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    get defaultNavigation() {
        return this._defaultNavigation;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Fill compact navigation children using the default navigation
            this._compactNavigation.forEach((compactNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === compactNavItem.id) {
                        compactNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

            const externalGroup = this.buildExternalModulesGroup();

            // Insert group after position 1 (after settings) if modules are set
            const withGroup = (arr: FuseNavigationItem[]) =>
                externalGroup ? [arr[0], arr[1], externalGroup, ...arr.slice(2)] : arr;

            const translateItem = (a: FuseNavigationItem) => ({
                ...a,
                title: this._translocoService.translate('navigation.' + a.title),
                children: a.children?.map((c) => ({
                    ...c,
                    title: this._translocoService.translate('navigation.' + c.title),
                })),
            });

            // Return the response
            return [
                200,
                {
                    compact: withGroup(this._compactNavigation).map(translateItem),
                    default: withGroup(this._defaultNavigation).map(translateItem),
                },
            ];
        });
    }

    private buildExternalModulesGroup(): FuseNavigationItem | null {
        const modules = this.externalModulesService.modules;
        if (modules.length === 0) return null;
        return {
            id: 'external-modules',
            title: 'external-modules',
            type: 'collapsable',
            icon: 'heroicons_outline:puzzle-piece',
            children: modules.map((m) => ({
                id: `external-module-${m.id}`,
                title: `modules.${m.id}`,
                type: 'basic' as const,
                icon: NavigationMockApi.EXTERNAL_MODULE_ICONS[m.id] ?? 'heroicons_outline:puzzle-piece',
                link: `/modules/${m.id}`,
            })),
        };
    }

    allChildrenHidden(item: FuseNavigationItem) {
        return item.children.every((x) => x.hidden && x.hidden(x));
    }
}
